import { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

// project imports
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { openSnackbar } from 'features/app/appSlice';
import { selectAjaxLoader, selectSnackbar } from 'features/app/appSelectors';

// material-ui
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  Fade,
  Grow,
  IconButton,
  Slide,
  SlideProps
} from '@mui/material';
import MuiSnackbar from '@mui/material/Snackbar';

// types
import { KeyedObject } from 'models';

// animation function
function TransitionSlideLeft(props: SlideProps) {
  return <Slide {...props} direction='left' />;
}

function TransitionSlideUp(props: SlideProps) {
  return <Slide {...props} direction='up' />;
}

function TransitionSlideRight(props: SlideProps) {
  return <Slide {...props} direction='right' />;
}

function TransitionSlideDown(props: SlideProps) {
  return <Slide {...props} direction='down' />;
}

function GrowTransition(props: SlideProps) {
  return <Grow {...props} />;
}

// animation options
const animation: KeyedObject = {
  SlideLeft: TransitionSlideLeft,
  SlideUp: TransitionSlideUp,
  SlideRight: TransitionSlideRight,
  SlideDown: TransitionSlideDown,
  Grow: GrowTransition,
  Fade
};

// ==============================|| SNACKBAR ||============================== //

const Snackbar = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const {
    actionButton,
    anchorOrigin,
    alert,
    close,
    message,
    open,
    transition,
    variant,
    autoHideDuration
  } = useAppSelector(selectSnackbar);

  const operationInProgress = useAppSelector(selectAjaxLoader);

  const handleClose = (_event: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;

    dispatch(openSnackbar({}));
  };

  return (
    <>
      {/* default snackbar */}
      {variant === 'default' && (
        <MuiSnackbar
          anchorOrigin={anchorOrigin}
          open={open}
          autoHideDuration={autoHideDuration}
          onClose={handleClose}
          message={message}
          TransitionComponent={animation[transition]}
          action={
            <>
              {actionButton && (
                <Button
                  color='secondary'
                  size='small'
                  onClick={handleClose}
                  disabled={operationInProgress}
                >
                  {t('common.button.back')}
                </Button>
              )}
              {close && (
                <IconButton
                  size='small'
                  aria-label='close'
                  color='inherit'
                  onClick={handleClose}
                  sx={{ mt: 0.25 }}
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              )}
            </>
          }
        />
      )}

      {/* alert snackbar */}
      {variant === 'alert' && (
        <MuiSnackbar
          TransitionComponent={animation[transition]}
          anchorOrigin={anchorOrigin}
          open={open}
          autoHideDuration={autoHideDuration}
          onClose={handleClose}
        >
          <Alert
            variant={alert.variant}
            color={alert.color}
            action={
              <>
                {actionButton && (
                  <Button
                    size='small'
                    onClick={handleClose}
                    sx={{ color: 'background.paper' }}
                    disabled={operationInProgress}
                  >
                    {t('common.button.back')}
                  </Button>
                )}
                {close && (
                  <IconButton
                    sx={{ color: 'background.paper' }}
                    size='small'
                    aria-label='close'
                    onClick={handleClose}
                    disabled={operationInProgress}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                )}
              </>
            }
            sx={{
              ...(alert.variant === 'outlined' && {
                bgcolor: 'background.paper'
              })
            }}
          >
            {message}
          </Alert>
        </MuiSnackbar>
      )}
    </>
  );
};

export default Snackbar;
